<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import Hls from 'hls.js';
  import { WebRTCAdaptor } from './webrtc_adaptor';
  import { channel } from '../Chat/chat-store';
  import { trackEvent } from '../../analytics';

  const dispatch = createEventDispatcher();

  export let muteAudio = true;

  let videoPlayer;
  let isVideoVisible = false;
  let videoStyle = '';

  let webRTCAdaptor = null;
  let hls;
  const hlsConfig = {};

  let currentQuality = 720;

  let timeoutId = null;
  let timePlayed = 0;

  $: userMuted = false;

  export const forceStreamQuality = (streamId, quality) => {
    currentQuality = Number(quality);
    webRTCAdaptor.forceStreamQuality(streamId, Number(quality));
  };

  export const disableStats = (streamId) => {
    webRTCAdaptor.disableStats(streamId);
  };

  export const init = (options, onInitCallback) => {
    const { playout, src } = options;
    if (playout === 'WebRTC') {
      initializeWebRtcAdaptor(src, onInitCallback);
    } else if (playout === 'HLS') {
      isManifestReady(src, onInitCallback);
    }
    videoPlayer.addEventListener('canplay', onCanPlayVideo);
    videoPlayer.addEventListener('error', onVideoError);
    videoPlayer.addEventListener('play', onVideoPlaying);
    videoPlayer.addEventListener('timeupdate', onVideoTimeUpdate);
  };

  export const play = (playout, streamName) => {
    if (playout === 'WebRTC') {
      webRTCAdaptor.play(streamName);
    } else if (playout === 'HLS') {
      videoPlayer.play();
    }
  };

  export const playVideo = () => {
    if (videoPlayer) {
      videoPlayer.play();
    }
  };

  onDestroy(() => {
    if (isVideoVisible && videoPlayer) {
      stop();
    }
  });

  export const stop = (playout, streamName) => {
    isVideoVisible = false;
    currentQuality = 720;
    if (playout === 'WebRTC') {
      webRTCAdaptor.stop(streamName);
      webRTCAdaptor.closeWebSocket(streamName);
      webRTCAdaptor.remoteVideo.srcObject = null;
      webRTCAdaptor = null;
    } else if (playout === 'HLS') {
      if (hls) {
        videoPlayer.pause();
        hls.destroy();
        hls = null;
      }
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
      timePlayed = 0;
    }

    videoPlayer.removeEventListener('canplay', onCanPlayVideo);
    videoPlayer.removeEventListener('error', onVideoError);
    videoPlayer.removeEventListener('play', onVideoPlaying);
    videoPlayer.removeEventListener('timeupdate', onVideoTimeUpdate);

    dispatch('video-done');
  };

  const isManifestReady = (src, onInitCallback) => {
    fetch(src).then((res) => {
      if (res.status === 404) {
        setTimeout(() => isManifestReady(src, onInitCallback), 1000);
      } else {
        initHLSPlayer(src, onInitCallback);
      }
    });
  };

  const onCanPlayVideo = (e) => {
    isVideoVisible = true;
    dispatch('video-ready', { paused: videoPlayer.paused });
  };

  const onVideoTimeUpdate = (e) => {
    if (!timeoutId) {
      timeoutId = setTimeout(() => {
        timePlayed += 30;
        timeoutId = null;
        trackEvent({
          action: 'Chat - Video chat - Progress',
          label: $channel.id,
          dimensions: {
            chatName: $channel.name,
            chatCategory: $channel.category,
            chatTimePlayed: timePlayed
          }
        });
      }, 1000 * 30);
    }
  };

  const onVideoPlaying = (e) => {
    dispatch('video-playing');
    if ($channel) {
      trackEvent({
        action: 'Chat - Video chat - Start',
        label: $channel.id,
        dimensions: {
          chatName: $channel.name,
          chatCategory: $channel.category
        }
      });
    }
  };

  const onVideoError = (e) => {
    console.log('VIDEO ERROR', e);
  };

  const initHLSPlayer = (src, onInitCallback, retries = 3) => {
    if (videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
      videoPlayer.src = src;
      if (onInitCallback) {
        onInitCallback();
      }
    } else if (Hls.isSupported()) {
      if (!hls) {
        hls = new Hls(hlsConfig);
        hls.attachMedia(videoPlayer);
        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          if (onInitCallback) {
            onInitCallback();
          }
        });
      }
      hls.loadSource(src);
    }
  };

  const initializeWebRtcAdaptor = (src, onInitCallback) => {
    const pc_config = {
      iceServers: [
        {
          urls: 'stun:stun1.l.google.com:19302'
        }
      ]
    };

    const sdpConstraints = {
      OfferToReceiveAudio: true,
      OfferToReceiveVideo: true
    };

    const mediaConstraints = {
      video: false,
      audio: false
    };

    webRTCAdaptor = new WebRTCAdaptor({
      websocket_url: src,
      mediaConstraints: mediaConstraints,
      peerconnection_config: pc_config,
      sdp_constraints: sdpConstraints,
      remoteVideoId: 'videoPlayer',
      isPlayMode: true,
      debug: false,
      candidateTypes: ['tcp', 'udp'],
      callback: (info, obj) => {
        if (info == 'initialized') {
          if (onInitCallback) {
            onInitCallback();
          }
        } else if (info == 'play_finished') {
          isVideoVisible = false;
          console.log('play_finished');
          dispatch('playfinsihed');
        } else if (info == 'closed') {
          console.log('closed');
        } else if (info == 'play_started') {
          webRTCAdaptor.enableStats(obj.streamId);
          webRTCAdaptor.getStreamInfo(obj.streamId);
        } else if (info == 'streamInformation') {
          var streamResolutions = new Array();

          obj['streamInfo'].forEach(function (entry) {
            //It's needs to both of VP8 and H264. So it can be dublicate
            if (!streamResolutions.includes(entry['streamHeight'])) {
              streamResolutions.push(entry['streamHeight']);
            }
          });
          streamResolutions = streamResolutions.sort(function (a, b) {
            return a - b;
          });
          console.log('streamResolutions', streamResolutions);
          dispatch('streamresolutions', streamResolutions);

          forceStreamQuality(obj.streamId, 720);
        } else if (info == 'ice_connection_state_changed') {
          console.log('iceConnectionState Changed: ', JSON.stringify(obj));
        } else if (info == 'updated_stats') {
          dispatch('updatedstats', { stats: obj, currentQuality });
        } else if (info == 'data_received') {
          console.log(
            'Data received: ' +
              obj.event.data +
              ' type: ' +
              obj.event.type +
              ' for stream: ' +
              obj.streamId
          );
        } else if (info == 'bitrateMeasurement') {
          if (obj.audioBitrate + obj.videoBitrate > obj.targetBitrate) {
            console.log('SLOW INTERNETT!', obj);
          }
        }
      },
      callbackError: function (error) {
        console.log('error callback: ' + JSON.stringify(error));
      }
    });
  };
</script>

<div class="player-container">
  <button
    class="mute-btn"
    class:visible={isVideoVisible}
    on:click={() => (userMuted = !userMuted)}
  >
    {#if userMuted}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="black"
        width="24px"
        height="24px"
        ><path d="M0 0h24v24H0z" fill="none" /><path
          d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
        /></svg
      >
    {:else}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="black"
        width="24px"
        height="24px"
        ><path d="M0 0h24v24H0z" fill="none" /><path
          d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
        /></svg
      >
    {/if}
  </button>
  <video
    id="videoPlayer"
    style={videoStyle}
    class:visible={isVideoVisible}
    autoplay
    playsinline
    muted={muteAudio || userMuted}
    bind:this={videoPlayer}
  />
</div>

<style src="./player.scss"></style>
